import { createPortal } from 'react-dom';
import { Button } from '@shared/components';
import { XIcon } from 'lucide-react';
import { useModalDialog } from './useModalDialog';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    className?: string;
    children: React.ReactNode;
    showCloseButton?: boolean;
    isFullscreen?: boolean;
    // A dialog must be named (WCAG 4.1.2): ariaLabelledBy for visible heading text, else
    // ariaLabel. Both optional only for backwards compatibility with unmigrated callers.
    ariaLabel?: string;
    ariaLabelledBy?: string;
    // Element to receive focus on open. A non-focusable target resolves to its first
    // focusable descendant (the span-wrapper pattern, since Button accepts no ref).
    initialFocusRef?: React.RefObject<HTMLElement | null>;
    // 'blur' frosts the page behind; 'dim' only darkens it so it stays readable.
    backdrop?: 'blur' | 'dim';
    // False keeps a click on the backdrop from closing (X and Escape still do).
    closeOnBackdropClick?: boolean;
    // Applied to the wrapper around children, for layouts that must fill the dialog.
    childrenClassName?: string;
}

const BACKDROP_CLASSES = {
    blur: 'bg-mp-overlay backdrop-blur-[4px]',
    dim: 'bg-mp-overlay',
} as const;

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    className = '',
    showCloseButton = true,
    isFullscreen = false,
    ariaLabel,
    ariaLabelledBy,
    initialFocusRef,
    backdrop = 'blur',
    closeOnBackdropClick = true,
    childrenClassName,
}) => {
    const { overlayRef, modalRef, childrenRef, closeButtonWrapperRef, handleOverlayKeyDown } =
        useModalDialog(isOpen, onClose, initialFocusRef);

    if (!isOpen) return null;

    const contentClasses = isFullscreen
        ? 'w-full h-full'
        : 'relative w-full rounded-xl bg-mp-surface';

    // Portalled to body so no ancestor's clip-path, transform or overflow can clip the overlay.
    // role="dialog" + aria-modal on the overlay make the whole subtree one dialog node.
    return createPortal(
        <div
            ref={overlayRef}
            className="fixed inset-0 flex items-center justify-center overflow-y-auto z-99999"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            onKeyDown={handleOverlayKeyDown}
        >
            {!isFullscreen && (
                <div
                    className={`absolute inset-0 h-full w-full ${BACKDROP_CLASSES[backdrop]}`}
                    onClick={closeOnBackdropClick ? onClose : undefined}
                ></div>
            )}
            <div
                ref={modalRef}
                tabIndex={-1}
                className={`${contentClasses}  ${className}`}
                onClick={(e) => e.stopPropagation()}
            >
                {showCloseButton && (
                    <span ref={closeButtonWrapperRef}>
                        <Button
                            onClick={onClose}
                            className="absolute p-1 right-2 top-2 z-999 flex items-center justify-center rounded-full text-mp-muted transition-colors hover:bg-hover-mp-surface hover:text-mp-foreground"
                            variant="clear"
                            size="none"
                            startIcon={<XIcon size={20} />}
                            ariaLabel="Close dialog"
                        />
                    </span>
                )}
                <div ref={childrenRef} className={childrenClassName}>
                    {children}
                </div>
            </div>
        </div>,
        document.body,
    );
};
